import { useState, useEffect } from "react";
import { supabase } from "../../../lib/supabase";

type Lang = "vi" | "kr";
const T = (lang: Lang, vi: string, kr: string) => (lang === "vi" ? vi : kr);

interface CustomerRow {
  email: string;
  name: string;
  phone: string;
  products: string[];
  latest_inquiry: string;
  total_inquiries: number;
}

interface Props {
  lang: Lang;
}

const CustomersManager = ({ lang }: Props) => {
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetchCustomers = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("inquiries")
      .select("name, email, phone, product_name, created_at")
      .order("created_at", { ascending: false });

    if (!data) { setLoading(false); return; }

    // Aggregate by email: 1 row per customer, update products list
    const map = new Map<string, CustomerRow>();
    for (const row of data) {
      const key = (row.email ?? "").toLowerCase().trim() || (row.phone ?? "").trim();
      if (!key) continue;
      if (map.has(key)) {
        const existing = map.get(key)!;
        if (row.product_name && !existing.products.includes(row.product_name)) {
          existing.products.push(row.product_name);
        }
        existing.total_inquiries += 1;
        if (row.created_at > existing.latest_inquiry) {
          existing.latest_inquiry = row.created_at;
          existing.name = row.name || existing.name;
          existing.phone = row.phone || existing.phone;
        }
      } else {
        map.set(key, {
          email: row.email ?? "",
          name: row.name ?? "",
          phone: row.phone ?? "",
          products: row.product_name ? [row.product_name] : [],
          latest_inquiry: row.created_at ?? "",
          total_inquiries: 1,
        });
      }
    }

    setCustomers(Array.from(map.values()).sort((a, b) => b.latest_inquiry.localeCompare(a.latest_inquiry)));
    setLoading(false);
  };

  useEffect(() => { fetchCustomers(); }, []);

  const filtered = customers.filter((c) => {
    const q = search.toLowerCase();
    return !q || c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q) || c.phone.includes(q) || c.products.some((p) => p.toLowerCase().includes(q));
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-800">{T(lang, "Danh Sách Khách Hàng", "고객 목록")}</h1>
          <p className="text-sm text-gray-500 mt-1">{T(lang, "Tổng hợp từ yêu cầu tư vấn – mỗi khách hàng 1 dòng, sản phẩm quan tâm tự động cập nhật", "상담 요청에서 집계 – 고객당 1행, 관심 제품 자동 업데이트")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchCustomers} className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">
            <i className="ri-refresh-line" />
            {T(lang, "Làm mới", "새로고침")}
          </button>
          <button
            onClick={() => {
              const headers = ["STT", "Họ tên", "Email", "Số điện thoại", "Sản phẩm quan tâm", "Số lần hỏi", "Lần cuối liên hệ"];
              const rows = filtered.map((c, i) => [
                i + 1,
                c.name || "",
                c.email || "",
                c.phone || "",
                c.products.join("; "),
                c.total_inquiries,
                c.latest_inquiry?.slice(0, 10) || "",
              ]);
              const csvContent = [headers, ...rows]
                .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
                .join("\n");
              const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `khach-hang-${new Date().toISOString().slice(0, 10)}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white text-sm rounded-xl hover:bg-green-700 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-excel-line" />
            {T(lang, "Xuất Excel", "엑셀 내보내기")}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="w-8 h-8 bg-[#004A99] rounded-lg flex items-center justify-center mb-2">
            <i className="ri-user-3-line text-white text-sm" />
          </div>
          <p className="text-xl font-black text-gray-800">{customers.length}</p>
          <p className="text-xs text-gray-400">{T(lang, "Tổng khách hàng", "전체 고객")}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center mb-2">
            <i className="ri-mail-check-line text-white text-sm" />
          </div>
          <p className="text-xl font-black text-gray-800">{customers.filter((c) => c.email).length}</p>
          <p className="text-xs text-gray-400">{T(lang, "Có email", "이메일 있음")}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center mb-2">
            <i className="ri-star-line text-white text-sm" />
          </div>
          <p className="text-xl font-black text-gray-800">{customers.filter((c) => c.total_inquiries > 1).length}</p>
          <p className="text-xs text-gray-400">{T(lang, "Hỏi nhiều lần", "반복 문의")}</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-xs mb-4">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
        <input
          type="text"
          placeholder={T(lang, "Tìm theo tên, email, SĐT, sản phẩm...", "이름, 이메일, 전화, 제품으로 검색...")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#004A99]"
        />
      </div>

      {loading ? (
        <div className="text-center py-20"><i className="ri-loader-4-line text-2xl text-[#004A99] animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
          <i className="ri-user-line text-4xl text-gray-200 block mb-4" />
          <p className="text-gray-400 text-sm">{T(lang, "Không có khách hàng nào", "고객이 없습니다")}</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">{T(lang, "Khách hàng", "고객")}</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">{T(lang, "Số điện thoại", "전화")}</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">{T(lang, "Sản phẩm quan tâm", "관심 제품")}</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">{T(lang, "Số lần hỏi", "문의 횟수")}</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">{T(lang, "Lần cuối", "최근")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((customer, idx) => (
                <tr key={idx} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-4">
                    <p className="text-sm font-semibold text-gray-900">{customer.name || "—"}</p>
                    <p className="text-xs text-gray-400">{customer.email || "—"}</p>
                  </td>
                  <td className="px-4 py-4">
                    <p className="text-sm text-gray-700">{customer.phone || "—"}</p>
                  </td>
                  <td className="px-4 py-4 max-w-[280px]">
                    {customer.products.length === 0 ? (
                      <span className="text-xs text-gray-400">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {customer.products.slice(0, 3).map((p, pi) => (
                          <span key={pi} className="text-[10px] bg-[#EDF2FB] text-[#004A99] px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                            {p.length > 30 ? p.slice(0, 30) + "..." : p}
                          </span>
                        ))}
                        {customer.products.length > 3 && (
                          <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">
                            +{customer.products.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${customer.total_inquiries > 1 ? "bg-amber-50 text-amber-700" : "bg-gray-100 text-gray-600"}`}>
                      {customer.total_inquiries} {T(lang, "lần", "회")}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <p className="text-xs text-gray-400 whitespace-nowrap">{customer.latest_inquiry?.slice(0, 10)}</p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CustomersManager;
